/**
 * The strategic link's boot (plan I2; spec §3.5, §10.1, §13, §14): in the Reach only, boot creates the strategic
 * client (`?worker=off` runs it in-process), `init`s it before the features, provides it as the `strategic`
 * service and binds it for the Reach subsystem (`subsystem.ts`). It also returns:
 *
 * - **the strategic feature** (order 170, after M3's `reach` 160): its `frame` drives the clock gate (paused frames
 *   forward nothing; docked holds do), and a tab hidden for `catchUp.hiddenResumeS` runs the catch-up flow, then
 *   re-enters the current system. The away report goes out as the `awayReport` bus event (I6 draws it).
 * - **the load wrapper** around the `careerReach` factory (D-TBD: no M2 save or load hook): before the career's
 *   Reach link applies a record, the Reach is re-initialized from the strategic save stored for that record (the
 *   save bridge's key) and catches up the real time since it was written; with none, a fresh Reach starts on the
 *   career's seed. After the link entered, the relay states are synced (`relays.ts`), then the re-init listeners
 *   (`onWorkerReinit`, M7: the Charter career bridge's story re-apply) are told once, with the worker's online day.
 * - **the save bridge** (`saveBridge.ts`), registered as the save storage's companion.
 *
 * The catch-up overlay is I6's styled one, loaded with a dynamic import on the first catch-up (the plain DOM line
 * shows until it arrives), so React is not pulled into this chunk.
 *
 * Boot imports this module dynamically (like the Reach runtime), so the main chunk does not grow.
 */
import type { GameContext, GameFeature, FrameInfo } from '../app/feature'
import { FEATURE_ORDER } from '../app/feature'
import type { Services } from '../app/services'
import type { CareerReachFactory } from '../career/flow'
import { HOLD, type CareerReachLink } from '../career/runtime'
import type { Career } from '../career/types'
import { logger } from '../core/logger'
import strategicJson from '../data/strategic/strategic.json'
import type { ReachCore, SystemSession } from '../reach/runtime/types'
import { openSaveStorage, setSaveCompanion, type CompanionStore, type SaveStorageConfig } from '../save/storage'
import { createStrategicClient, type StrategicClient, type StrategicClientMode } from '../strategic/client'
import type { AwayReport, InitResult, StoryView, StrategicSave } from '../strategic/types'
import { createCatchUpFlow, createDomCatchUpOverlay, createLazyCatchUpOverlay, type CatchUpFlow } from './catchUpFlow'
import { createClockGate, watchVisibility, type ClockGate } from './clockGate'
import { applyWorkerRelays, pushReachRelays } from './relays'
import { companionKeys, createSaveBridge, loadStrategicSave } from './saveBridge'
import { bindStrategicLink, queueHandoff } from './subsystem'

declare module '../app/services' {
  interface ServiceMap {
    /** M4 (plan I2): the strategic client, in the Reach only. */
    strategic: StrategicClient
  }
}

/** `?worker=off` runs the strategic core on the main thread. */
export function strategicModeOf(search: string): StrategicClientMode {
  return new URLSearchParams(search).get('worker') === 'off' ? 'inProcess' : 'worker'
}

/** The Reach runtime parts the link reads. */
export type StrategicReach = Pick<ReachCore, 'galaxy' | 'state'> & { readonly session: Pick<SystemSession, 'layout'> }

export interface StrategicBootDeps {
  services: Services
  reach: StrategicReach
  search: string
  /** Enables the dev commands (dev and test builds). */
  dev: boolean
  /** Where the catch-up overlay goes (`#overlay`). */
  overlayRoot: HTMLElement
  /** Wall clock in ms (default `Date.now`). */
  now?: () => number
  /** Makes the client (tests); default `createStrategicClient({ mode })`. */
  createClient?: (mode: StrategicClientMode) => StrategicClient
  /** Opens the save storage the loads read (tests); default `openSaveStorage`. */
  openCompanion?: (cfg: SaveStorageConfig) => Promise<CompanionStore | undefined>
}

/** M7: the worker was re-initialized for a career being entered (after the career is set and the system entered). */
export interface WorkerReinit {
  /** True when the Reach came from the record's strategic companion, false for a fresh Reach. */
  readonly fromSave: boolean
  /** The worker's story clock now (null when the worker has no story surface). */
  readonly view: Pick<StoryView, 'onlineDay'> | null
}

export interface StrategicBoot {
  readonly client: StrategicClient
  /** The strategic feature (order 170): add it to the feature list. */
  readonly feature: GameFeature
  readonly gate: ClockGate
  readonly flow: CatchUpFlow
  /** Wraps the `careerReach` factory with the strategic load (see the file header). */
  wrapCareerReach(inner: CareerReachFactory): CareerReachFactory
  /** M7: called once after every career enter re-initialized the worker; returns the unsubscribe. */
  onWorkerReinit(fn: (e: WorkerReinit) => void): () => void
  /** The last away report (the debug API). */
  readonly lastReport: AwayReport | null
  /** Debug: loads treat the stored Reach as `seconds` older than it is. */
  setSavedAtOffset(seconds: number): void
}

let current: StrategicBoot | null = null

/** The booted link, or null outside the Reach (the debug API). */
export function strategicBoot(): StrategicBoot | null {
  return current
}

function defaultOpenCompanion(cfg: SaveStorageConfig): Promise<CompanionStore | undefined> {
  return openSaveStorage(cfg).then((o) => o.storage.companion)
}

/** Creates, initializes and provides the strategic client (see the file header). */
export async function bootStrategic(deps: StrategicBootDeps): Promise<StrategicBoot> {
  const now = deps.now ?? Date.now
  const mode = strategicModeOf(deps.search)
  const client = deps.createClient?.(mode) ?? createStrategicClient({ mode })
  const galaxySeed = deps.reach.galaxy.seed >>> 0
  await client.init({ galaxySeed, seed: galaxySeed, dev: deps.dev, sources: 'm3' })
  logger.info(`Strategic core running (${client.mode})`)

  let ctxRef: GameContext | null = null
  let lastReport: AwayReport | null = null
  let savedAtOffsetS = 0
  let companion: Promise<CompanionStore | undefined> | null = null
  const openCompanion = deps.openCompanion ?? defaultOpenCompanion
  const reinitListeners = new Set<(e: WorkerReinit) => void>()

  const flow = createCatchUpFlow({
    client,
    capS: strategicJson.catchUp.capS,
    reportMinS: strategicJson.catchUp.hiddenResumeS,
    // I6's styled overlay is a lazy chunk (React); the plain line shows until it has loaded.
    overlay: createLazyCatchUpOverlay(createDomCatchUpOverlay(deps.overlayRoot), () =>
      import('../ui/strategic/CatchUpOverlay').then((m) => m.createStyledCatchUpOverlay(deps.overlayRoot)),
    ),
    report: (r) => {
      lastReport = r
      ctxRef?.bus.emit('awayReport', r)
    },
  })

  const currentSystem = (): string | null => deps.reach.session.layout?.systemId ?? null

  /** Enters the current system unless the client already has it local (runs on the hand-off chain). */
  async function ensureEntered(): Promise<void> {
    const id = currentSystem()
    if (id !== null && client.manifest?.systemId !== id) await client.enterSystem(id)
  }

  const gate = createClockGate({
    target: client,
    hiddenResumeS: strategicJson.catchUp.hiddenResumeS,
    now,
    onLongHidden: (hiddenS) => {
      void queueHandoff(async () => {
        await flow.run(hiddenS)
        await ensureEntered()
      })
    },
    // The dashboard in Pause mode pauses the whole world, the Reach day included (card D1).
    frozen: () => ctxRef !== null && ctxRef.holds.has(HOLD.dashboard),
  })

  let unwatch: (() => void) | null = null
  const feature: GameFeature = {
    name: 'strategic',
    order: FEATURE_ORDER.strategic,
    init(ctx: GameContext): void {
      ctxRef = ctx
      if (typeof document !== 'undefined') unwatch = watchVisibility(gate, document)
    },
    frame(_ctx: GameContext, f: FrameInfo): void {
      gate.frame(f)
    },
    dispose(): void {
      unwatch?.()
      unwatch = null
    },
  }

  /** Re-initializes the Reach for a career being entered: from its stored strategic save, else fresh. */
  async function loadFor(career: Career, cfg: SaveStorageConfig, savedAt?: string): Promise<boolean> {
    companion ??= openCompanion(cfg).catch((err: unknown) => {
      logger.warn('strategic link: the save storage did not open; the Reach starts fresh', err)
      return undefined
    })
    const env = await loadStrategicSave(await companion, ...companionKeys(career.seed, career.gameTime, savedAt))
    const seed = career.seed >>> 0
    if (env !== null && (await initFrom(env.save, seed)) !== null) {
      await flow.run((now() - env.savedAt) / 1000 + savedAtOffsetS)
      return true
    }
    await client.init({ galaxySeed, seed, dev: deps.dev, sources: 'm3' })
    return false
  }

  async function initFrom(save: StrategicSave, seed: number): Promise<InitResult | null> {
    try {
      return await client.init({ galaxySeed, seed, save, dev: deps.dev, sources: 'm3' })
    } catch (err) {
      logger.warn('strategic link: the stored Reach did not load; starting a fresh Reach', err)
      return null
    }
  }

  /** Tells the re-init listeners (the worker's story view gives its online day; none without a story surface). */
  async function notifyReinit(fromSave: boolean): Promise<void> {
    let view: Pick<StoryView, 'onlineDay'> | null = null
    try {
      view = { onlineDay: (await client.storyView()).onlineDay }
    } catch (err: unknown) {
      logger.debug('strategic link: no story view after the re-init', err)
    }
    const e: WorkerReinit = { fromSave, view }
    for (const fn of reinitListeners) {
      try {
        fn(e)
      } catch (err: unknown) {
        logger.warn('strategic link: a re-init listener failed', err)
      }
    }
  }

  function wrapCareerReach(inner: CareerReachFactory): CareerReachFactory {
    return (target, data): CareerReachLink => {
      const link = inner(target, data)
      return {
        state: () => link.state(),
        async enter(block, career, savedAt): Promise<void> {
          let fromSave = false
          await queueHandoff(async () => {
            fromSave = await loadFor(career, data.career.save, savedAt)
          })
          await link.enter(block, career, savedAt)
          await queueHandoff(async () => {
            await ensureEntered()
            const view = await client.refreshGalaxyView()
            if (fromSave) applyWorkerRelays(deps.reach.state, deps.reach.galaxy, view.relays)
            else await pushReachRelays(deps.reach.state, view.relays, client)
            if (reinitListeners.size > 0) await notifyReinit(fromSave)
          })
        },
        entered: () => link.entered(),
        respawnReady: () => link.respawnReady(),
      }
    }
  }

  setSaveCompanion(createSaveBridge({ client: () => (client.initialized ? client : null), now }))
  deps.services.provide('strategic', client)
  bindStrategicLink(client)
  // The Reach entered its first system before the client existed.
  void queueHandoff(ensureEntered)

  const boot: StrategicBoot = {
    client,
    feature,
    gate,
    flow,
    wrapCareerReach,
    onWorkerReinit(fn: (e: WorkerReinit) => void): () => void {
      reinitListeners.add(fn)
      return () => reinitListeners.delete(fn)
    },
    get lastReport(): AwayReport | null {
      return lastReport
    },
    setSavedAtOffset(seconds: number): void {
      savedAtOffsetS = Number.isFinite(seconds) ? seconds : 0
    },
  }
  current = boot
  return boot
}
